import NotificationsIcon from "@mui/icons-material/Notifications";
import Badge from "@mui/material/Badge";
import React, { forwardRef, memo, useCallback, useMemo } from "react";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import RotaItem from "./components/DropdownRotaItem.tsx";
import DropdownSection from "./components/DropdownSection.tsx";
import DropdownSectionCombined from "./components/DropdownSectionCombined.tsx";
import { NavbarItems } from "./constants/Dropdown.constants.tsx";
import {
    NavbarItemsEnum,
    ViagemItemsEnum,
} from "./interfaces/Dropdown.enum.ts";

interface DropdownEmbarquesProps {
    ref: React.RefObject<HTMLDivElement>;
}

const DropdownEmbarques = forwardRef<HTMLDivElement, DropdownEmbarquesProps>(
    (props, ref) => {
        const {
            qtPedidosAguardando,
            numeroCte,
            handleSetContextoEmbarque,
            coresEmpresa,
            dadosFilial,
            cteDivergentes,
        } = useProfile();

        const onClick = useCallback(
            (contexto?: string) => {
                if (!!contexto) {
                    handleSetContextoEmbarque(contexto);
                }
            },
            [handleSetContextoEmbarque]
        );

        const viagemEPedidosSections =
            NavbarItems[NavbarItemsEnum.VIAGEMEPEDIDOS].sections;

        const viagemItems = viagemEPedidosSections[ViagemItemsEnum.VIAGENS].items;

        const pedidoItems = viagemEPedidosSections[ViagemItemsEnum.PEDIDOS].items;

        const diariaEmbarquesItems =
            viagemEPedidosSections[ViagemItemsEnum.DIARIAEMBARQUES].items;

        const prestacaoServicoItems =
            viagemEPedidosSections[ViagemItemsEnum.PRESTACAOSERVICO].items;

        const logisticaDeTrafegoItems =
            viagemEPedidosSections[ViagemItemsEnum.LOGISTICATRAFEGO].items;

        const divergenciasSATItems =
            viagemEPedidosSections[ViagemItemsEnum.DIVERGENCIASSAT].items;

        const onTime = viagemEPedidosSections[ViagemItemsEnum.ON_TIME].items;

        const memoizedViagemEPedidosSections = useMemo(() => {
            const filteredDadosFilial =
                dadosFilial?.filter((item) => item.contexto !== "templateEmbarque") ||
                [];
            const filialItems =
                filteredDadosFilial?.map((item) => ({
                    label: item.nomeFilial,
                    rota: "/viagens/" + item.contexto,
                    contextoEmbarque: item.contexto,
                })) || [];

            const combinedItems = [...filialItems, ...viagemItems];
            return combinedItems.map((item, index) => (
                <div
                    key={`viagem-${item.rota}-${item.label}`}
                    className="botao-menu-cte"
                >
                    <RotaItem
                        key={`viagemEPedidos-${index}`}
                        item={item}
                        onClick={() => onClick(item.contextoEmbarque)}
                    />
                    {item.label === "Aguardando CTE" && numeroCte > 0 && (
                        <Badge
                            key={`badge-${item.rota}`}
                            badgeContent={numeroCte}
                            color="primary"
                        >
                            <NotificationsIcon className="icone-notificao" color="inherit" />
                        </Badge>
                    )}
                </div>
            ));
        }, [dadosFilial, coresEmpresa.textoBotaoNormal, onClick]);

        const memorizedPedidoItems = useMemo(() => {
            return pedidoItems.map((item) => (
                <div key={`pedido-${item.rota}`} className="botao-menu-cte">
                    <RotaItem key={`pedido-${item.rota}`} item={item} />
                    {item.label === "Gerenciamento de Pedido" &&
                        qtPedidosAguardando > 0 && (
                            <Badge badgeContent={qtPedidosAguardando} color="primary">
                                <NotificationsIcon
                                    className="icone-notificao"
                                    color="inherit"
                                />
                            </Badge>
                        )}
                </div>
            ));
        }, [qtPedidosAguardando, pedidoItems]);

        const memorizedDiariaEmbarquesItems = useMemo(() => {
            return diariaEmbarquesItems.map((item) => (
                <RotaItem key={`diaria-${item.rota}`} item={item} />
            ));
        }, [diariaEmbarquesItems]);

        const memoizedPrestacaoServico = useMemo(() => {
            return prestacaoServicoItems.map((item) => (
                <RotaItem key={`prestacao-${item.rota}`} item={item} />
            ));
        }, [prestacaoServicoItems]);

        const memoizedDivergenciasSAT = useMemo(() => {
            return divergenciasSATItems.map((item) => (
                <div key={`pedido-${item.rota}`} className="botao-menu-cte">
                    <RotaItem key={`divergencias-${item.rota}`} item={item} />
                    {item.label === "Divergências SAT" &&
                        !!cteDivergentes && (
                            <Badge badgeContent={cteDivergentes} color="primary">
                                <NotificationsIcon
                                    className="icone-notificao"
                                    color="inherit"
                                />
                            </Badge>
                        )}
                </div>
            ));
        }, [divergenciasSATItems]);

        const memoizedLogisticaDeTrafego = useMemo(() => {
            return logisticaDeTrafegoItems.map((item) => (
                <RotaItem key={`logistica-${item.rota}`} item={item} />
            ));
        }, [logisticaDeTrafegoItems]);

        const memorizedOnTime = useMemo(() => {
            return onTime.map((item) => (
                <RotaItem key={`ontime-${item.rota}`} item={item} />
            ));
        }, [onTime]);

        return (
            <div ref={ref} className="dropdown-content-emb">
                <div className="separador-menu" />
                <div className="content-menu-rows">
                    <DropdownSection
                        titleSection={
                            viagemEPedidosSections[ViagemItemsEnum.VIAGENS].titleSection
                        }
                        icon={viagemEPedidosSections[ViagemItemsEnum.VIAGENS].icon}
                    >
                        {memoizedViagemEPedidosSections}
                    </DropdownSection>
                    <DropdownSectionCombined
                        titleSection1={
                            viagemEPedidosSections[ViagemItemsEnum.PEDIDOS].titleSection
                        }
                        children1={memorizedPedidoItems}
                        icon1={viagemEPedidosSections[ViagemItemsEnum.PEDIDOS].icon}
                        titleSection2={
                            viagemEPedidosSections[ViagemItemsEnum.DIARIAEMBARQUES]
                                .titleSection
                        }
                        children2={memorizedDiariaEmbarquesItems}
                        icon2={viagemEPedidosSections[ViagemItemsEnum.DIARIAEMBARQUES].icon}
                        children3={memoizedPrestacaoServico}
                        icon3={viagemEPedidosSections[ViagemItemsEnum.PRESTACAOSERVICO].icon}
                        titleSection3={
                            viagemEPedidosSections[ViagemItemsEnum.PRESTACAOSERVICO]
                                .titleSection
                        }
                        children4={memoizedLogisticaDeTrafego}
                        icon4={viagemEPedidosSections[ViagemItemsEnum.LOGISTICATRAFEGO].icon}
                        titleSection4={
                            viagemEPedidosSections[ViagemItemsEnum.LOGISTICATRAFEGO]
                                .titleSection
                        }
                    />
                    <DropdownSectionCombined
                        titleSection1={
                            viagemEPedidosSections[ViagemItemsEnum.ON_TIME].titleSection
                        }
                        icon1={viagemEPedidosSections[ViagemItemsEnum.ON_TIME].icon}
                        children1={memorizedOnTime}
                        titleSection2={viagemEPedidosSections[ViagemItemsEnum.DIVERGENCIASSAT]
                            .titleSection}
                        children2={memoizedDivergenciasSAT}
                        icon2={viagemEPedidosSections[ViagemItemsEnum.DIVERGENCIASSAT].icon}
                    />
                </div>
            </div>
        );
    }
);

export default memo(DropdownEmbarques);
